import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { http, inr, computeTotals, requestErrorMessage } from "@/lib/erp";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Search, Plus, Minus, Trash2, ShoppingCart, Package, Receipt, ScanLine, PauseCircle, ListChecks } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function Pos() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [walkin, setWalkin] = useState(null);
  const [search, setSearch] = useState("");
  const [cart, setCart] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [payMode, setPayMode] = useState("Cash");
  const [received, setReceived] = useState("");
  const [charging, setCharging] = useState(false);
  const [scan, setScan] = useState("");
  const [holds, setHolds] = useState([]);
  const [holdOpen, setHoldOpen] = useState(false);
  const [prereqLoading, setPrereqLoading] = useState(true);
  const [prereqError, setPrereqError] = useState("");

  const loadHolds = async () => {
    try { const r = await http.get("/pos/holds"); setHolds(r.data); }
    catch (e) { toast.warning(requestErrorMessage(e, "load held bills")); }
  };
  const loadPrerequisites = async () => {
    setPrereqLoading(true); setPrereqError("");
    try {
      const [productRes, customerRes] = await Promise.all([http.get("/products"), http.get("/customers")]);
      setProducts(productRes.data); setCustomers(customerRes.data);
    } catch (e) { setPrereqError(requestErrorMessage(e, "load products and customers")); }
    finally { setPrereqLoading(false); }
    try {
      const { data } = await http.get("/pos/walkin");
      setWalkin(data); setCustomerId((current) => current || data.id);
    } catch (e) { toast.warning(requestErrorMessage(e, "load walk-in customer")); }
    await loadHolds();
  };
  useEffect(() => { loadPrerequisites(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return products.slice(0, 60);
    return products.filter((p) => (p.name + " " + (p.code || "") + " " + (p.composition || "")).toLowerCase().includes(q)).slice(0, 60);
  }, [products, search]);

  const addToCart = (p) => {
    setCart((prev) => {
      const ex = prev.find((x) => x.product_id === p.id);
      if (ex) return prev.map((x) => x.product_id === p.id ? { ...x, qty: x.qty + 1 } : x);
      return [...prev, {
        product_id: p.id, name: p.name, hsn: p.hsn || "", batch_no: p.batch_no || "",
        expiry_date: p.expiry_date || "", unit: p.unit || "PCS", qty: 1,
        rate: p.selling_price || p.ptr || p.mrp || 0, mrp: p.mrp || 0, discount_pct: 0, gst_rate: p.gst_rate || 0,
      }];
    });
  };
  const setQty = (pid, delta) => setCart((prev) => prev.flatMap((x) => {
    if (x.product_id !== pid) return [x];
    const q = x.qty + delta;
    return q <= 0 ? [] : [{ ...x, qty: q }];
  }));
  const removeItem = (pid) => setCart((prev) => prev.filter((x) => x.product_id !== pid));

  const doScan = async (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    const code = scan.trim();
    if (!code) return;
    setScan("");
    try {
      const { data } = await http.get("/pos/scan", { params: { code } });
      addToCart(data);
      toast.success(`Added ${data.name}`);
    } catch (e) {
      toast.error(requestErrorMessage(e, `find product for code "${code}"`));
    }
  };

  const holdBill = async () => {
    if (cart.length === 0) return toast.error("Cart is empty");
    try {
      await http.post("/pos/holds", {
        items: cart, customer_id: customerId, customer_name: selCustomer?.name || "",
        payment_mode: payMode, label: `${cart.length} item(s) · ${inr(totals.grand)}`,
      });
      toast.success("Bill held");
      setCart([]); setReceived("");
      await loadHolds();
    } catch (e) { toast.error(requestErrorMessage(e, "hold bill")); }
  };

  const resumeHold = async (h) => {
    try {
      await http.delete(`/pos/holds/${h.id}`);
      setCart(h.items || []);
      if (h.customer_id) setCustomerId(h.customer_id);
      if (h.payment_mode) setPayMode(h.payment_mode);
      setHoldOpen(false);
      toast.success("Bill resumed");
      await loadHolds();
    } catch (e) { toast.error(requestErrorMessage(e, "resume held bill")); }
  };
  const discardHold = async (id) => {
    try { await http.delete(`/pos/holds/${id}`); toast.success("Held bill discarded"); await loadHolds(); }
    catch (e) { toast.error(requestErrorMessage(e, "discard held bill")); }
  };

  const selCustomer = customers.find((c) => c.id === customerId) || walkin;
  const posCode = selCustomer?.state_code || "36";
  const totals = computeTotals(cart, posCode, { round_off_enabled: true });

  const charge = async () => {
    if (cart.length === 0) return toast.error("Cart is empty");
    if (!customerId) return toast.error("Select a customer");
    setCharging(true);
    try {
      const payload = {
        customer_id: customerId,
        place_of_supply: selCustomer?.state || "Telangana",
        place_of_supply_code: posCode,
        items: cart,
        amount_received: received === "" ? totals.grand : +received,
        payment_mode: payMode,
      };
      const { data } = await http.post("/invoices", payload);
      toast.success(`Sale complete · ${data.invoice_no}`);
      setCart([]); setReceived("");
      navigate(`/sales/invoices/${data.id}?print=1&format=thermal`);
    } catch (e) { toast.error(requestErrorMessage(e, "complete sale")); }
    finally { setCharging(false); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <div className="mb-5 flex items-center gap-2 flex-wrap">
        <Receipt className="h-6 w-6 text-brand-600" /><h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">POS Billing</h1>
        <Button size="sm" variant="outline" className="ml-auto" onClick={() => { loadHolds(); setHoldOpen(true); }} data-testid="pos-held-bills-button">
          <ListChecks className="h-4 w-4 mr-1" /> Held Bills{holds.length ? ` (${holds.length})` : ""}
        </Button>
      </div>
      {prereqError && <div className="mb-4 flex items-center justify-between gap-3 rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700"><span>{prereqError}</span><Button size="sm" variant="outline" onClick={loadPrerequisites}>Retry prerequisite data</Button></div>}
      {prereqLoading && <div className="mb-4 text-sm text-muted-foreground">Loading products and customers…</div>}

      <div className="grid lg:grid-cols-[1fr_400px] gap-6 items-start">
        {/* Product picker */}
        <div className="space-y-4">
          <div className="relative">
            <ScanLine className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-brand-600" />
            <Input placeholder="Scan barcode — item drops into cart on Enter" value={scan}
              onChange={(e) => setScan(e.target.value)} onKeyDown={doScan} className="pl-9" data-testid="pos-scan-input" />
          </div>
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Search products by name, code, salt…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="pos-search" />
          </div>
          <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
            {filtered.map((p) => (
              <button key={p.id} onClick={() => addToCart(p)} data-testid={`pos-product-${p.id}`}
                className="text-left p-3 rounded-lg border border-slate-200 bg-white hover:border-brand-400 hover:shadow-sm transition-all">
                <div className="font-medium text-sm line-clamp-2 min-h-[2.5rem]">{p.name}</div>
                <div className="text-[11px] text-muted-foreground mt-1">{p.pack_size} · GST {p.gst_rate}%</div>
                <div className="flex items-center justify-between mt-2">
                  <span className="font-mono font-semibold text-brand-700">{inr(p.selling_price || p.ptr || p.mrp || 0)}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${Number(p.stock) > 0 ? "bg-emerald-50 text-emerald-600" : "bg-rose-50 text-rose-600"}`}>Stk {p.stock}</span>
                </div>
              </button>
            ))}
            {filtered.length === 0 && <div className="col-span-full text-center py-12 text-muted-foreground"><Package className="h-8 w-8 mx-auto mb-2 opacity-40" />No products</div>}
          </div>
        </div>

        {/* Cart */}
        <Card className="p-4 lg:sticky lg:top-4 flex flex-col" style={{ maxHeight: "calc(100vh - 120px)" }} data-testid="pos-cart">
          <div className="flex items-center gap-2 pb-3 border-b"><ShoppingCart className="h-4 w-4 text-brand-600" /><span className="font-heading font-semibold">Cart</span><span className="ml-auto text-xs text-muted-foreground">{cart.length} item(s)</span></div>

          <div className="flex-1 overflow-y-auto -mx-1 px-1 py-2 space-y-2 min-h-[120px]">
            {cart.map((it) => (
              <div key={it.product_id} className="flex items-center gap-2 text-sm" data-testid={`pos-cart-item-${it.product_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{it.name}</div>
                  <div className="text-[11px] text-muted-foreground font-mono">{inr(it.rate)} × {it.qty}</div>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => setQty(it.product_id, -1)} className="p-1 rounded hover:bg-slate-100" data-testid={`pos-dec-${it.product_id}`}><Minus className="h-3.5 w-3.5" /></button>
                  <span className="w-6 text-center tabular-nums">{it.qty}</span>
                  <button onClick={() => setQty(it.product_id, 1)} className="p-1 rounded hover:bg-slate-100" data-testid={`pos-inc-${it.product_id}`}><Plus className="h-3.5 w-3.5" /></button>
                </div>
                <div className="w-20 text-right font-mono">{inr(it.qty * it.rate)}</div>
                <button onClick={() => removeItem(it.product_id)} className="p-1 rounded hover:bg-rose-50"><Trash2 className="h-3.5 w-3.5 text-rose-500" /></button>
              </div>
            ))}
            {cart.length === 0 && <div className="text-center text-sm text-muted-foreground py-8">Tap products to add</div>}
          </div>

          <div className="border-t pt-3 space-y-3">
            <div>
              <Label className="text-xs">Customer</Label>
              <Select value={customerId} onValueChange={setCustomerId}>
                <SelectTrigger className="mt-1 h-9" data-testid="pos-customer-select"><SelectValue placeholder="Select customer" /></SelectTrigger>
                <SelectContent>
                  {walkin && <SelectItem value={walkin.id}>Walk-in Customer</SelectItem>}
                  {customers.filter((c) => c.customer_type !== "Walk-in").map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="text-sm space-y-1">
              <div className="flex justify-between text-muted-foreground"><span>Taxable</span><span className="font-mono">{inr(totals.subtotal)}</span></div>
              <div className="flex justify-between text-muted-foreground"><span>GST</span><span className="font-mono">{inr(totals.totalTax)}</span></div>
              <div className="flex justify-between font-bold text-base pt-1 border-t"><span>Total</span><span className="font-mono" data-testid="pos-total">{inr(totals.grand)}</span></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Payment</Label>
                <Select value={payMode} onValueChange={setPayMode}>
                  <SelectTrigger className="mt-1 h-9" data-testid="pos-payment-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>{["Cash", "UPI", "Card", "Bank"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Received</Label>
                <Input type="number" value={received} onChange={(e) => setReceived(e.target.value)} placeholder={totals.grand.toFixed(2)} className="mt-1 h-9" data-testid="pos-amount-received" />
              </div>
            </div>
            <Button className="w-full" disabled={charging || prereqLoading || !!prereqError || cart.length === 0} onClick={charge} data-testid="pos-charge-button">
              {charging ? "Processing…" : `Charge & Print · ${inr(totals.grand)}`}
            </Button>
            <Button variant="outline" className="w-full" disabled={cart.length === 0} onClick={holdBill} data-testid="pos-hold-button">
              <PauseCircle className="h-4 w-4 mr-1" /> Hold / Park Bill
            </Button>
          </div>
        </Card>
      </div>

      <Dialog open={holdOpen} onOpenChange={setHoldOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Held Bills</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {holds.length === 0 && <div className="text-center text-sm text-muted-foreground py-8">No held bills</div>}
            {holds.map((h) => (
              <div key={h.id} className="flex items-center gap-2 border rounded-lg p-3" data-testid={`pos-hold-${h.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm truncate">{h.customer_name || "Walk-in"} · {h.label}</div>
                  <div className="text-[11px] text-muted-foreground">{new Date(h.created_at).toLocaleString()}</div>
                </div>
                <Button size="sm" onClick={() => resumeHold(h)} data-testid={`pos-resume-${h.id}`}>Resume</Button>
                <button onClick={() => discardHold(h.id)} className="p-1.5 rounded hover:bg-rose-50" data-testid={`pos-discard-${h.id}`}><Trash2 className="h-4 w-4 text-rose-500" /></button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
